import {Anchor, Button, Navbar, Stack, createStyles} from '@mantine/core';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {MODEL_HUB_NAVBAR_WIDTH_PX} from './ModelHubConstants';
import type {ModelHubPage} from './ModelHubPages';
import ModelHubProfileSection from './ModelHubProfileSection';

interface ModelHubNavItem {
  icon: BenchlingIconName;
  label: string;
  page: ModelHubPage | null;
}

const NAV_ITEMS: Array<ModelHubNavItem> = [
  {label: 'Models', icon: 'hexagon-nodes', page: 'models'},
  {label: 'Predictions', icon: 'lightbulb-outline', page: 'predictions'},
  {label: 'Batches', icon: 'square-list-outline', page: 'batches'},
];

interface ModelHubNavbarProps {
  activePage: ModelHubPage;
  onContactUs: () => void;
  onNavigate: (page: ModelHubPage) => void;
  onNewPrediction: () => void;
}

export default function ModelHubNavbar({
  activePage,
  onContactUs,
  onNavigate,
  onNewPrediction,
}: ModelHubNavbarProps): JSX.Element {
  const {classes, cx} = useStyles();

  const trackSidebarClick = (page: ModelHubPage): void => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIDEBAR_NAV_CLICKED, {page});
  };

  return (
    <Navbar className={classes.navbar}>
      <Navbar.Section>
        <Anchor className={classes.brandGroup} href="/">
          <BenchlingIcon className={classes.brandIcon} icon="benchling-logo" />
          <div>
            <div className={classes.brandName}>Benchling</div>
            <div className={classes.brandProduct}>Model Hub</div>
          </div>
        </Anchor>
      </Navbar.Section>

      <div className={classes.divider} />

      <Navbar.Section className={classes.ctaSection}>
        <Button
          className={classes.ctaButton}
          fullWidth={true}
          leftIcon={<BenchlingIcon icon="plus" />}
          onClick={onNewPrediction}
          radius="sm"
          variant="primary"
        >
          New prediction
        </Button>
      </Navbar.Section>

      <Navbar.Section grow={true}>
        <Stack spacing={3}>
          {NAV_ITEMS.map((item) => (
            <button
              key={item.label}
              aria-current={item.page === activePage ? 'page' : undefined}
              className={cx(classes.navItem, item.page === activePage && classes.navItemActive)}
              onClick={
                item.page != null
                  ? () => {
                      trackSidebarClick(item.page!);
                      onNavigate(item.page!);
                    }
                  : undefined
              }
              type="button"
            >
              <BenchlingIcon className={classes.navItemIcon} icon={item.icon} />
              {item.label}
            </button>
          ))}
        </Stack>
      </Navbar.Section>

      <Navbar.Section>
        <ModelHubProfileSection onContactUs={onContactUs} />
      </Navbar.Section>
    </Navbar>
  );
}

const useStyles = createStyles((theme) => ({
  navbar: {
    borderRight: `1px solid ${theme.colors.gray[3]}`,
    minWidth: MODEL_HUB_NAVBAR_WIDTH_PX,
    padding: '20px 16px 14px',
    width: MODEL_HUB_NAVBAR_WIDTH_PX,
  },
  brandGroup: {
    alignItems: 'center',
    color: 'inherit',
    display: 'flex',
    gap: 11,

    '&:hover': {
      textDecoration: 'none',
    },
  },
  brandIcon: {
    color: theme.colors.blue[8],
    fontSize: 22,
  },
  brandName: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.1,
  },
  brandProduct: {
    color: theme.colors.gray[6],
    fontSize: 13,
    lineHeight: 1.1,
  },
  divider: {
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    margin: '14px -16px 16px',
  },
  ctaSection: {
    marginBottom: 22,
  },
  ctaButton: {
    '&&': {
      backgroundColor: theme.colors.blue[8],
      fontSize: 14.5,
      fontWeight: 600,
      height: 'auto',
      padding: '11px 14px',
    },

    '&:hover': {
      backgroundColor: theme.colors.blue[9],
    },
  },
  navItem: {
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    borderRadius: 8,
    color: theme.colors.gray[8],
    cursor: 'pointer',
    display: 'flex',
    fontSize: 14.5,
    fontWeight: 500,
    gap: 13,
    padding: '10px 14px',
    textAlign: 'left' as const,
    width: '100%',

    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  navItemActive: {
    backgroundColor: theme.colors.gray[0],
    color: theme.colors.blue[8],
    fontWeight: 600,
  },
  navItemIcon: {
    fontSize: 18,
  },
}));
